import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Compass, Tent, MailOpen, MessageSquare, Hammer, Users, Megaphone, Plane, Sailboat, Mountain, TreePine, Caravan, Laptop } from "lucide-react";
import AdvertiseRequestModal from "@/components/AdvertiseRequestModal";
import { useSeo } from "@/hooks/useSeo";

const HERO = "https://static.prod-images.emergentagent.com/jobs/ce29a23f-0299-4779-bff9-21549e2de9ce/images/b181c35c9c575205e62185a04e74ebc6609c47895d632563bbab3434a3d6b3c9.png";
const NEWSLETTER_TEX = "https://static.prod-images.emergentagent.com/jobs/ce29a23f-0299-4779-bff9-21549e2de9ce/images/9508a38de5e882f3476098dac976f44aa6f60114963f4891e09e045b45a9d0bc.png";

// Narrow black fir-tree silhouette used as a tasteful separator between the
// two mobile-view advertisement visuals on the landing CTA.
const FirTreeSketch = ({ className = "h-32 w-auto", ...rest }) => (
  <svg viewBox="0 0 24 64" className={className} aria-hidden="true" {...rest}>
    <path
      d="M12 2 L7 14 L9 14 L5 26 L8 26 L4 38 L9 38 L9 50 L7 50 L7 58 L17 58 L17 50 L15 50 L15 38 L20 38 L16 26 L19 26 L15 14 L17 14 Z"
      fill="#0a0a0a"
      stroke="#0a0a0a"
      strokeWidth="0.5"
    />
  </svg>
);

const features = [
  { icon: Users, title: "Nomad profile", body: "Tell the community where you are right now, and what you are looking for or want to share with others." },
  { icon: MessageSquare, title: "Discussion forum", body: "Open threads about visas, off-grid living, land searches, slow-travel routes — written by people actually doing it." },
  { icon: MailOpen, title: "Direct messaging", body: "Quietly reach out to a fellow nomad. No public broadcasts, no algorithmic feed." },
  { icon: Tent, title: "Community ads", body: "Looking to join a project, cohousing experiment, eco-build? Post a seeking ad. Selling a build? Share price, location, permits." },
  { icon: Laptop, title: "Digital Nomads", body: "For nomads who work as they roam: find your next base, swap tips on connectivity and visas, and post ads for the housing or co-living spot you're seeking." },
  { icon: MailOpen, title: "Monthly newsletter", body: "A slow, considered letter — once a month. New members get one free inclusion to introduce themselves." },
  { icon: Compass, title: "No noise", body: "We do not run a feed. There is no scroll. There are only people, places, and intentions." },
];

export default function Landing() {
  useSeo({
    title: "Nomad Life — the borderless community for full-time nomads",
    description: "Nomad Life is a curated members-only community for full-time nomads, van-lifers, off-grid builders and everyone still looking for where to land. Swap routes, discuss visas, share listings and message fellow travellers.",
  });
  const { user } = useAuth();
  const [showAdvertise, setShowAdvertise] = useState(false);
  const location = useLocation();

  // Smooth-scroll to a section when the URL carries a hash (e.g. /#pricing).
  // Used by the "Join the journey" CTAs in the global navbar so visitors land
  // on the pricing block immediately.
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.replace(/^#/, "");
    const t = setTimeout(() => {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
    return () => clearTimeout(t);
  }, [location.hash]);
  return (
    <div className="fade-in">
      {/* HERO */}
      <section className="relative">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(180deg, rgba(12,20,16,0.6) 0%, rgba(12,20,16,0.92) 80%, var(--hh-bg) 100%), url(${HERO})`,
          }}
        />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-20 pb-32 lg:pt-32 lg:pb-48">
          <span className="label-eyebrow" data-testid="hero-eyebrow">a quiet platform · for nomad living</span>

          <div className="mt-6 grid lg:grid-cols-[1fr_auto_1fr] gap-8 lg:gap-12 items-start">
            {/* LEFT — original nomad pitch */}
            <div data-testid="hero-left">
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl max-w-2xl leading-[1.02]">
                For those still <em className="text-[var(--hh-accent)] not-italic">looking</em><br/>
                for where to land.
              </h1>
              <p className="mt-8 text-lg text-[var(--hh-sage)] leading-relaxed">
                Nomad Life is a community for individuals who have chosen the nomad life — whether you are still travelling, or already searching for a home location to settle on. No house-sitting, no free labour. Just real people, real projects, real conversations.
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                {user ? (
                  <Link to="/dashboard" className="btn-primary" data-testid="hero-cta-dashboard">Enter the community</Link>
                ) : (
                  <>
                    <Link to="/#pricing" className="btn-primary" data-testid="hero-cta-register">Join the journey</Link>
                    <Link to="/login" className="btn-ghost" data-testid="hero-cta-login">I already have an account</Link>
                  </>
                )}
              </div>
            </div>

            {/* VERTICAL DIVIDER — only visible on desktop, only when the user is signed-out */}
            {!user && (
              <div
                className="hidden lg:block w-px self-stretch"
                style={{ background: "linear-gradient(180deg, transparent 0%, var(--hh-border) 18%, var(--hh-border) 82%, transparent 100%)" }}
                aria-hidden="true"
                data-testid="hero-divider"
              />
            )}

            {/* RIGHT — Vanlife pitch (shown to visitors only) */}
            {!user && (
              <div data-testid="hero-right-vanlife">
                <span className="label-eyebrow flex items-center gap-2 text-[var(--hh-accent)]">
                  <Caravan size={14} /> Vanlife
                </span>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-5xl mt-4 leading-[1.06]">
                  Living on the road,<br/>
                  <em className="text-[var(--hh-accent)] not-italic">on your own terms.</em>
                </h2>
                <p className="mt-6 text-[var(--hh-sage)] leading-relaxed">
                  Plan your next journey with our Travel Planner, find your next van — or sell your old one — through Community Ads, and meet like-minded travellers who&apos;ll share the road with you.
                </p>
                <ul className="mt-6 space-y-2 text-sm text-[var(--hh-cream)]">
                  <li>· Plan routes and stops with the Travel Planner</li>
                  <li>· Find or sell your next van in Community Ads</li>
                  <li>· Meet like-minded nomads to share the road</li>
                </ul>
                <div className="mt-8">
                  <Link to="/register" className="btn-ghost" data-testid="hero-cta-vanlife">Start your vanlife journey</Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* VALUE PROPS — asymmetric bento.
          Row 1: Nomad profile (4) + Discussion forum (2)
          Row 2: Direct messaging (2) + Community ads (2 — half of original) + Digital Nomads (2)
          Row 3: Monthly newsletter (3) + No noise (3) */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 -mt-12 lg:-mt-20 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 stagger">
          {features.map((f, i) => {
            const Icon = f.icon;
            const spans = ["md:col-span-4", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-2", "md:col-span-3", "md:col-span-3"];
            const span = spans[i] || "md:col-span-3";
            return (
              <div key={i} className={`card-hh ${span}`} data-testid={`feature-card-${i}`}>
                <Icon size={22} color="var(--hh-accent)" />
                <h3 className="font-display text-2xl mt-4">{f.title}</h3>
                <p className="mt-3 text-[var(--hh-sage)] leading-relaxed text-sm">{f.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* RULES STRIP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24">
        <div className="grid md:grid-cols-3 gap-8">
          <div>
            <span className="label-eyebrow">House rules</span>
            <h2 className="font-display text-4xl mt-3 leading-tight">A small set of agreements that keep this place useful.</h2>
          </div>
          <div className="md:col-span-2 grid sm:grid-cols-2 gap-6 text-sm text-[var(--hh-sage)]">
            <div className="border-l border-[var(--hh-border)] pl-5">
              <p className="text-[var(--hh-cream)] font-semibold mb-1">No free labour</p>
              <p>Seeking-to-join ads are welcome. Listings that ask for unpaid work are not.</p>
            </div>
            <div className="border-l border-[var(--hh-border)] pl-5">
              <p className="text-[var(--hh-cream)] font-semibold mb-1">No house-sitting or pet-sitting</p>
              <p>This isn&apos;t the right home for those services — there are better platforms for that.</p>
            </div>
            <div className="border-l border-[var(--hh-border)] pl-5">
              <p className="text-[var(--hh-cream)] font-semibold mb-1">Construction sales — full transparency</p>
              <p>If you list a finished or in-progress project for sale, include price, location, and permit status.</p>
            </div>
            <div className="border-l border-[var(--hh-border)] pl-5">
              <p className="text-[var(--hh-cream)] font-semibold mb-1">Speak as yourself</p>
              <p>Your profile uses your legal name. The internet does not need another anonymous forum.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-32" id="pricing">
        <div className="text-center max-w-2xl mx-auto">
          <span className="label-eyebrow text-[var(--hh-accent)]">First Thousand</span>
          <h2 className="font-display text-4xl sm:text-5xl mt-4">Join the first thousand nomads for €10.</h2>
          <p className="mt-5 text-[var(--hh-sage)]">A full year on Nomad Life for the price of a long coffee.</p>
        </div>
        <div className="mt-14 max-w-xl mx-auto">
          <div className="card-hh border-[var(--hh-accent)] relative" data-testid="pricing-12m" style={{ borderColor: "var(--hh-accent)" }}>
            <span className="absolute -top-3 left-6 px-3 py-1 text-xs label-eyebrow bg-[var(--hh-bg)] border border-[var(--hh-accent)] text-[var(--hh-accent)]">
              First Thousand · intro offer
            </span>
            <span className="label-eyebrow" style={{ color: "var(--hh-accent)" }}>Annual Nomad Pass</span>
            <h3 className="font-display text-4xl mt-3">12 months · €10</h3>
            <p className="mt-3 text-sm text-[var(--hh-sage)]">
              <span className="text-[var(--hh-cream)]" data-testid="pricing-12m-monthly">€0.83/month</span>
              <span className="ml-2">·  €10 for your first year</span>
              <span className="block mt-1 text-xs">Renews automatically at €48/year · VAT included · Cancel anytime</span>
            </p>
            <ul className="mt-8 space-y-3 text-sm text-[var(--hh-cream)]">
              <li>· Verified member badge</li>
              <li>· Full forum &amp; messaging access</li>
              <li>· Post community &amp; property ads</li>
              <li>· One free newsletter inclusion</li>
              <li>· Blog function — document your journey privately or share it</li>
              <li>· Travel Planner tool for planning your journeys</li>
            </ul>
            <Link to={user ? "/billing" : "/checkout?plan=12m"} className="btn-primary mt-10 inline-block w-full text-center" data-testid="pricing-12m-cta">Claim my place</Link>
          </div>
        </div>

        {/* Company plans — only Annual is sold now. The 6-month company plan
            was archived on 2026-09-24 alongside the 6-month nomad plan. */}
        <div className="mt-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="label-eyebrow">For companies &amp; operators</span>
            <h3 className="font-display text-3xl sm:text-4xl mt-4">Running an eco-village, retreat, farm, or community?</h3>
            <p className="mt-4 text-[var(--hh-sage)]">For registered companies operating intentional-living spaces, recovery retreats, eco-villages, farms and similar. EU companies that enter a valid VAT number at checkout get automatic reverse-charge.</p>
            <p className="mt-3 text-sm text-[var(--hh-accent)]">Capped at one thousand founding members — then the price returns to €90/year.</p>
          </div>
          <div className="mt-12 max-w-xl mx-auto">
            <div className="card-hh border-[var(--hh-accent)]" data-testid="pricing-company-12m" style={{ borderColor: "var(--hh-accent)" }}>
              <span className="label-eyebrow" style={{ color: "var(--hh-accent)" }}>Annual pass</span>
              <h3 className="font-display text-4xl mt-3">Company · 12 months · €45</h3>
              <p className="mt-3 text-sm text-[var(--hh-sage)]">€3.75/month · renews automatically · VAT included</p>
              <ul className="mt-8 space-y-3 text-sm text-[var(--hh-cream)]">
                <li>· Listed in Operators &amp; Communities directory</li>
                <li>· Company badge on profile</li>
                <li>· Full forum, messaging &amp; ads access</li>
                <li>· Blog function — document your journey privately or share it</li>
                <li>· Travel Planner tool for planning your journeys</li>
                <li>· One full year of community visibility</li>
              </ul>
              <Link to={user ? "/billing" : "/checkout?plan=company_12m"} className="btn-primary mt-10 inline-block" data-testid="pricing-company-12m-cta">Choose this plan</Link>
            </div>
          </div>
        </div>

        {/* Service-provider advertise CTA — flanked by service visuals.
            Desktop: 3-col grid (image | text | image).
            Mobile: text first, then a row of [image | fir tree | image] below. */}
        <div className="mt-20 grid md:grid-cols-[1fr_minmax(0,640px)_1fr] gap-6 md:gap-10 items-center" data-testid="service-provider-cta">
          <div
            className="hidden md:block aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
            style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_nomad-nest-13/artifacts/ybuc71oa_Copilot_20260601_125728.png)" }}
            role="img"
            aria-label="Camper van at a forest campsite at sunset"
            data-testid="service-provider-visual-left"
          />

          <div className="text-center">
            <div className="flex items-center justify-center gap-4 sm:gap-8 mb-6 text-[var(--hh-accent)]" data-testid="service-provider-icons">
              <Plane size={32} strokeWidth={1.4} aria-hidden="true" />
              <Sailboat size={32} strokeWidth={1.4} aria-hidden="true" />
              <TreePine size={32} strokeWidth={1.4} aria-hidden="true" />
              <Mountain size={32} strokeWidth={1.4} aria-hidden="true" />
              <Tent size={32} strokeWidth={1.4} aria-hidden="true" />
            </div>
            <p className="text-[var(--hh-cream)] text-lg leading-relaxed">
              Providing services for airport transfer, boat shuttle, camping site or alike — and want to have an ad published on this platform?
            </p>
            <button
              onClick={() => setShowAdvertise(true)}
              className="btn-primary mt-6 inline-flex items-center gap-2"
              data-testid="landing-advertise-btn"
            >
              <Megaphone size={14}/> Send your ad request
            </button>
          </div>

          <div
            className="hidden md:block aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
            style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_nomad-nest-13/artifacts/zaaw9nym_Copilot_20260601_125816.png)" }}
            role="img"
            aria-label="Long-tail boat moored at a tropical island"
            data-testid="service-provider-visual-right"
          />

          {/* Mobile-only row: shown beneath the text on small screens. */}
          <div className="md:hidden grid grid-cols-2 gap-3 mt-2" data-testid="service-provider-mobile-row">
            <div
              className="aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
              style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_nomad-nest-13/artifacts/ybuc71oa_Copilot_20260601_125728.png)" }}
              role="img"
              aria-label="Camper van at a forest campsite at sunset"
              data-testid="service-provider-visual-left-mobile"
            />
            <div
              className="aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
              style={{ backgroundImage: "url(https://customer-assets.emergentagent.com/job_nomad-nest-13/artifacts/zaaw9nym_Copilot_20260601_125816.png)" }}
              role="img"
              aria-label="Long-tail boat moored at a tropical island"
              data-testid="service-provider-visual-right-mobile"
            />
          </div>
        </div>
      </section>

      {/* NEWSLETTER PROMO */}
      <section className="relative mt-32">
        <div className="absolute inset-0 bg-cover bg-center opacity-30" style={{ backgroundImage: `url(${NEWSLETTER_TEX})` }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(90deg, var(--hh-bg) 30%, rgba(12,20,16,0.4) 100%)" }} />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="label-eyebrow">The Letter</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-4">One newsletter, once a month.</h2>
            <p className="mt-5 text-[var(--hh-cream)] leading-relaxed" style={{ fontStyle: "italic" }} data-testid="newsletter-peace-note">
              Published on the platform. No e-mails are sent to you with respect to your peace of mind.
            </p>
            <p className="mt-4 text-[var(--hh-sage)] leading-relaxed">A single, considered letter from the community. New members get one free inclusion — introduce yourself, share what you&apos;re seeking, link your project. Additional inclusions are available at a small fee.</p>
          </div>
          <div className="card-hh">
            <h4 className="font-display text-2xl">What a free inclusion looks like</h4>
            <div className="divider my-5" />
            <p className="text-sm text-[var(--hh-sage)] leading-relaxed italic">
              &ldquo;Mira, 32, currently in northern Portugal. Looking to join a small permaculture build in southern Europe for spring–autumn. Quiet, hands-on, no bullshit. mira@... &rdquo;
            </p>
            <p className="text-xs label-eyebrow mt-6">your one inclusion · included with membership</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA — flanked by photorealistic Nomad Life visuals on desktop,
          stacked below the text on mobile. */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mt-24 mb-24">
        <div className="grid md:grid-cols-[1fr_minmax(0,560px)_1fr] gap-6 md:gap-10 items-center" data-testid="final-cta-row">
          {/* Desktop left visual */}
          <div
            className="hidden md:block aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
            style={{ backgroundImage: "url(/img/landing-fire-circle.jpg)" }}
            role="img"
            aria-label="Intergenerational group around a campfire in a forest, one playing acoustic guitar"
            data-testid="final-cta-visual-left"
          />

          {/* Center text + CTA */}
          <div className="card-hh text-center py-16">
            <h2 className="font-display text-4xl sm:text-5xl">Find your people. Find your place.</h2>
            <p className="mt-4 max-w-xl mx-auto text-[var(--hh-sage)]">No feed. No metrics. Just a small, slow community of nomads making real decisions about where to live — and how to live.</p>
            <div className="mt-8 flex justify-center gap-4 flex-wrap">
              <Link to={user ? "/dashboard" : "/register"} className="btn-primary" data-testid="footer-cta-register">{user ? "Enter the community" : "Create your nomad profile"}</Link>
            </div>
          </div>

          {/* Desktop right visual */}
          <div
            className="hidden md:block aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
            style={{ backgroundImage: "url(/img/landing-a-frame-cabin.jpg)" }}
            role="img"
            aria-label="Wooden A-frame house with fir-tree forest behind and a small farming plot to the right, sunlit from the right"
            data-testid="final-cta-visual-right"
          />

          {/* Mobile-only row — both visuals shown beneath the text */}
          <div className="md:hidden grid grid-cols-2 gap-3 mt-2" data-testid="final-cta-mobile-row">
            <div
              className="aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
              style={{ backgroundImage: "url(/img/landing-fire-circle.jpg)" }}
              role="img"
              aria-label="Intergenerational group around a campfire in a forest, one playing acoustic guitar"
              data-testid="final-cta-visual-left-mobile"
            />
            <div
              className="aspect-[4/5] border border-[var(--hh-border)] bg-cover bg-center"
              style={{ backgroundImage: "url(/img/landing-a-frame-cabin.jpg)" }}
              role="img"
              aria-label="Wooden A-frame house with fir-tree forest behind and a small farming plot to the right, sunlit from the right"
              data-testid="final-cta-visual-right-mobile"
            />
          </div>
        </div>
      </section>

      {showAdvertise && <AdvertiseRequestModal onClose={() => setShowAdvertise(false)} />}
    </div>
  );
}
